
import React from "react";
import { FormControls, i18nManager } from "../../../../../i18n";
import { CSSTransition } from 'react-transition-group';
import { ApiResponseData, BaseService, FaqService, SupportRequestCommentService, ISupportRequestComment, ISupportRequest, IPlatform, SanitizedResponse, SupportRequestService, ISupportRequestParticipant, SupportRequestStatus, SupportCategoryService, ISupportCategory, IProfile, AdministrationService, IAdministrator, ConsusService } from "../../../../../services";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, Transition, Table, DataManageRef, Column, Checkbox, NoseurObject, AlertInterface, NoseurButtonOnClickEvent, Classname, FileInputItemOptions, FileInputMode, Orientation, TextAreaInput, FileInputManageRef, PopoverManageRef, Popover, FileHelper, BoolHelper } from "@rivtn/noseur";
import { AttachmentType } from "../../../../../services/hera/SupportRequestCommentService";

export interface TicketRequestProps {
    profile: IProfile;
    platform: IPlatform;
}

function TicketRequest(props: TicketRequestProps) {
    const { platform, profile } = props;
    const navigate = ReactRouterDom.useNavigate();
    const { ticketId } = ReactRouterDom.useParams();
    const consusService = ConsusService.getInstance();
    const [comment, setComment] = React.useState<string>("");
    const commentTextAreaContainerRef = React.useRef<any>(null);
    const sendButtonManageRef = React.useRef<ButtonManageRef>(null);
    const [showFilters, setShowFilters] = React.useState<boolean>();
    const [isPreview, setIsPreview] = React.useState<boolean>(false);
    const supportRequestService = SupportRequestService.getInstance();
    const administrationService = AdministrationService.getInstance();
    const fileInputManageRef = React.useRef<FileInputManageRef>(null);
    const [status, setStatus] = React.useState<SupportRequestStatus>();
    const supportCategoryService = SupportCategoryService.getInstance();
    const attachPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [supportRequest, setSupportRequest] = React.useState<ISupportRequest>();
    const supportRequestCommentService = SupportRequestCommentService.getInstance();
    const [beforeFetchContent, setBeforeFetchContent] = React.useState<boolean>(false);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.ticket_requests.request;
    const requestsLabels = i18nManager.Labels.dashboard.platform_management.management.ticket_requests;
    const [supportRequestComments, setSupportRequestComments] = React.useState<ApiResponseData<ISupportRequestComment>>({});
    labels.status_control.options = requestsLabels.filters.form.find((f) => f.name === "status")?.options?.slice(1);
    labels.platform_control.options = Utils.__CACHED_PLATFORMS.filter((p) => p.support_enabled);

    React.useEffect(() => {
        if (!ticketId) {
            navigate({
                search: "platform_id=" + platform.platform_id,
                pathname: `/dashboard/management/ticket-requests/requests`,
            });
            return;
        }
        fetchRequest();
    }, []);

    return (<div className="charon-messages">
        <div className="management">
            {!supportRequest
                ? (Elements.LOADING_STATE)
                : (<div className="sup-ticket" style={{ flex: 1 }}>
                    <div className="header">
                        <span className="t lined-limit">{supportRequest.title}</span>
                        <span className="l lined-limit">{Utils.toSaneSentenceFormat((requestsLabels.table.statuses as any)[status?.toLowerCase() ?? ""] ?? "")} - {Utils.formatToFineDate2(supportRequest.created_at)}</span>
                    </div>
                    <div className="container">
                        <div className="support-comments">
                            {(!supportRequestComments.content)
                                ? (Elements.LOADING_STATE)
                                : (<>
                                    <div className="comments">
                                        {supportRequestComments.content?.map((comment, index) => (<div key={comment.external_id} className={Classname.build("comment", { "c-highlight": comment.user.external_id === profile.external_id })} ref={(r) => {
                                            if (r && beforeFetchContent && (index + 1) === ((supportRequestComments.content?.length ?? 0))) {
                                                setTimeout(() => r?.scrollIntoView(), 300);
                                                setBeforeFetchContent(false);
                                            }
                                        }}>
                                            <div className="user">
                                                <img alt={comment.user.first_name} src={comment.user.profile_picture_location} />
                                                <div className="details">
                                                    <span className="name">{comment.user.first_name} {comment.user.last_name}</span>
                                                    <span className="date">{Utils.formatToFineDate(comment.created_at)}</span>
                                                </div>
                                            </div>
                                            <div className="content">
                                                <div className="text" dangerouslySetInnerHTML={{ __html: Utils.MARKDOWN_CONVERTER.makeHtml(comment.comment.replace(/\n/g, "<br />")) }} />
                                                {comment.attachments?.length > 0 && (<div className="attachments">
                                                    <div className="title">
                                                        <i className="fa fa-paperclip" />
                                                        <span>{comment.attachments.length} {labels.attachments}</span>
                                                    </div>
                                                    <div className="items attachment-items">
                                                        {comment.attachments.map((attachment, index) => (<a key={"attachment-" + index} className="item" href={attachment.location} rel="noreferrer" target="_blank">
                                                            <i className={Classname.build("fa", {
                                                                "fa-music": (attachment.type === AttachmentType.AUDIO),
                                                                "fa-image": (attachment.type === AttachmentType.IMAGE),
                                                                "fa-video": (attachment.type === AttachmentType.VIDEO),
                                                                "fa-file": (attachment.type === AttachmentType.UNKNOWN),
                                                            })} />
                                                            <div>
                                                                <span className="name">{attachment.name}</span>
                                                                <span className="size">{attachment.size}</span>
                                                            </div>
                                                        </a>))}
                                                    </div>
                                                </div>)}
                                            </div>
                                        </div>))}
                                    </div>
                                    {!BoolHelper.equalsAny(status, [SupportRequestStatus.CLOSED, SupportRequestStatus.RESOLVED, SupportRequestStatus.UNRESOLVED]) && (<div className="message-box">
                                        {!isPreview && (<TextAreaInput ref={commentTextAreaContainerRef} className="comment-area" scheme={Schemes.RIVTN_QUIRINUS} placeholder={labels.controls.reply}
                                            defaultValue={comment} onInputComplete={setComment} borderless />)}
                                        {isPreview && (<div className="comment-area" dangerouslySetInnerHTML={{ __html: Utils.MARKDOWN_CONVERTER.makeHtml(comment) }} />)}
                                        <div className="bottom">
                                            <Checkbox scheme={Schemes.RIVTN_QUIRINUS} label={labels.controls.send_ticket_thread} />
                                            <div className="controls">
                                                <Button scheme={Schemes.RIVTN_QUIRINUS} onClick={() => setIsPreview(!isPreview)} text={isPreview ? labels.controls.edit : labels.controls.preview} textOnly fillOnHover />
                                                <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.controls.attach} leftIcon="fa fa-paperclip"
                                                    onClick={(e: any) => attachPopOverManageRef.current?.toggle(e)} outlined fillOnHover />
                                                <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.controls.send} manageRef={sendButtonManageRef} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={sendComment}
                                                    outlined fillOnHover />
                                            </div>
                                        </div>
                                        <FileInput dragAndDropRefs={[commentTextAreaContainerRef]} orientation={Orientation.VERTICAL} mode={FileInputMode.PREVIEW}
                                            onDragComplete={() => commentTextAreaContainerRef.current.style.borderColor = "transparent"} emptyTemplate={() => null}
                                            manageRef={fileInputManageRef} onDragBegin={() => commentTextAreaContainerRef.current.style.borderColor = "#0FA883"}
                                            maxFileSize={2000000} itemTemplate={(options: FileInputItemOptions) => {
                                                const fileKey = `![${options.file.name}-${options.index}]`;
                                                if (options.file.type.includes("image")) {
                                                    if (comment && !comment.includes(fileKey)) {
                                                        const fileMarkdownString = `${fileKey}(${URL.createObjectURL(options.file)})\n`;
                                                        const cursorPosition = commentTextAreaContainerRef.current?.selectionStart;
                                                        let updatedComment = Utils.stringSplice(comment, cursorPosition, fileMarkdownString);
                                                        commentTextAreaContainerRef.current.value = updatedComment;
                                                        setComment(updatedComment);
                                                    }
                                                }
                                                return (<div key={"attachment-" + options.file.name} className="item">
                                                    <i className={Classname.build("fa", {
                                                        "fa-image": (options.file.type.includes("image")),
                                                        "fa-music": (options.file.type.includes("audio")),
                                                        "fa-video": (options.file.type.includes("video")),
                                                        "fa-file": !(options.file.type.includes("image") || options.file.type.includes("audio") || options.file.type.includes("video")),
                                                    })} />
                                                    <div>
                                                        <span className="name">{options.file.name}</span>
                                                        <span className="size">{options.formattedSize}</span>
                                                    </div>
                                                    <i className="fa fa-times" onClick={() => {
                                                        if (comment && options.file.type.includes("image")) {
                                                            const fileKeyIndex = comment.indexOf(fileKey);
                                                            const filePreviewDeletionFrom = fileKeyIndex;
                                                            const filePreviewDeletionTo = comment.indexOf(")", fileKeyIndex + 1) + 2;
                                                            let updatedComment = Utils.stringCleave(comment, filePreviewDeletionFrom, filePreviewDeletionTo);
                                                            commentTextAreaContainerRef.current.value = updatedComment;
                                                            setComment(updatedComment);
                                                        }
                                                        options.onRemove(options.file);
                                                    }} />
                                                </div>);
                                            }} onValidationFail={(file: File, error: any) => {
                                                BaseService.reportError(error);
                                                return true;
                                            }}
                                            noDragAndDrop concatNewFile multiple />
                                    </div>)}
                                </>)}
                        </div>
                        <div className="controls">
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                {[labels.status_control, labels.platform_control, labels.assignee_control].map((control: FormControls, index: number) => {
                                    return (<div key={index} className="sub-filter" style={{ marginBottom: 15 }}>
                                        <span>{control.title}</span>
                                        {Elements.buildConfirmDialogForm([control], (name: string, value: any, searching: any) => {
                                            if (searching) {
                                                if (name.includes("assignee")) {
                                                    administrationService.queryAdministrator({ email: value.value, status: "APPROVED" }).then(({ apiResponse }: SanitizedResponse<IAdministrator>) => {
                                                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((admin: IAdministrator) => {
                                                            return {
                                                                value: {
                                                                    email: admin.email,
                                                                    last_name: admin.last_name,
                                                                    first_name: admin.first_name,
                                                                    external_id: admin.external_id,
                                                                    profile_picture_location: admin.profile_picture_location,
                                                                },
                                                                label: `${admin.first_name} ${admin.last_name} (${admin.email})`,
                                                            };
                                                        }));
                                                    }).catch(BaseService.reportError);
                                                }
                                                return;
                                            }
                                            (supportRequest as any)[name] = value;
                                        }, { ...supportRequest, assignee: (supportRequest.assignee ? `${supportRequest.assignee.first_name} ${supportRequest.assignee.last_name} (${supportRequest.assignee.email})` : undefined) }, undefined, { marginTop: 0 })}
                                    </div>);
                                })}
                            </div>
                            <Button raised scheme={Schemes.RIVTN_QUIRINUS} text={labels.update_ticket} style={{ width: "100%" }}
                                disabled={BoolHelper.equalsAny(status, [SupportRequestStatus.RESOLVED])}
                                loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => updateSupportRequest(e.manageRef)} />
                        </div>
                    </div>
                </div>)}
        </div>

        <Popover manageRef={attachPopOverManageRef} className="dashboard-popover" dismissOnClick>
            <span style={{ justifyContent: "flex-start" }} className="popover-link" onClick={() => fileInputManageRef.current?.select()}><i className="fa fa-file" style={{ marginRight: 20 }} />{labels.controls.documents}</span>
            <span style={{ justifyContent: "flex-start" }} className="popover-link" onClick={() => fileInputManageRef.current?.select()}><i className="fa fa-image" style={{ marginRight: 20 }} />{labels.controls.medias}</span>
        </Popover>
    </div>);

    function fetchRequest() {
        supportRequestService.getSupportRequest(ticketId!).then(({ apiResponse: { data } }: SanitizedResponse<ISupportRequest>) => {
            data!.platform_id = data?.platform.platform_id!;
            setSupportRequest(data);
            setStatus(data?.status);
            fetchRequestComments();
            if (!data?.assignee) {
                data!.assignee = {
                    middle_name: "",
                    email: profile.email,
                    last_name: profile.last_name,
                    first_name: profile.first_name,
                    external_id: profile.external_id,
                    profile_picture_location: profile.profile_picture_location,
                };
                updateSupportRequest(undefined, data);
            }
        }).catch(BaseService.reportError);
    }

    function fetchRequestComments() {
        supportRequestCommentService.querySupportRequestComments({ sort: "id,ASC", "support_request.external_id": ticketId!, size: 400 }).then(({ sanitized }: SanitizedResponse<any>) => {
            setSupportRequestComments(sanitized);
        }).catch(BaseService.reportError);
    }

    function updateSupportRequest(manageRef?: ButtonManageRef, extras?: any) {
        manageRef?.setLoadingState(true);
        supportRequestService.updateSupportRequest({ ...supportRequest!, ...extras }).then(({ apiResponse: { data, message } }: SanitizedResponse<ISupportRequest>) => {
            Siren.alert(message);
            setStatus(data?.status);
        }).catch(BaseService.reportError).finally(() => manageRef?.setLoadingState(false));
    }

    async function sendComment() {
        let newComment = comment;
        const payload: any = {
            attachments: [],
            support_request_id: ticketId,
            platform_id: platform?.platform_id,
            user: {
                middle_name: "",
                email: profile.email,
                last_name: profile.last_name,
                first_name: profile.first_name,
                profile_picture_location: profile.profile_picture_location
            }
        };
        if (!comment && !fileInputManageRef.current?.files().length) {
            BaseService.reportError(labels.missing_comment_attachment as any);
            return;
        }
        const successfulUploads: string[] = [];
        sendButtonManageRef.current?.setLoadingState(true);
        try {
            for (let index = 0; index < fileInputManageRef.current!.files().length; index++) {
                const file = fileInputManageRef.current!.files()[index];
                let type = "UNKNOWN";
                if (file.type.includes("audio")) type = "AUDIO";
                if (file.type.includes("video")) type = "VIDEO";
                if (file.type.includes("image")) type = "IMAGE";
                const fileKey = `![${file.name}-${index}]`;
                const uploadResult = (await consusService.uploadBinary({ file, fullname: `/hera/support/comment/attachment/${ticketId}-${file.name}` }))?.sanitized;
                successfulUploads.push(uploadResult.relative_path);
                if (type == "IMAGE") {
                    const fileKeyIndex = newComment.indexOf(fileKey);
                    const filePreviewDeletionFrom = fileKeyIndex + fileKey.length + 1;
                    const filePreviewDeletionTo = newComment.indexOf(")", fileKeyIndex + 1);
                    newComment = Utils.stringSplice(newComment, filePreviewDeletionFrom, uploadResult.consus_download_url, filePreviewDeletionTo);
                } else {
                    payload.attachments.push({
                        type,
                        name: file.name,
                        location: uploadResult.consus_download_url,
                        size: FileHelper.humanFileSize(file.size),
                    });
                }
            }
            payload.comment = newComment;
            await supportRequestCommentService.createSupportTicketComment(payload);
            setComment("");
            console.log("WE HERE", commentTextAreaContainerRef.current);
            fileInputManageRef.current?.clear();
            setBeforeFetchContent(true);
            setTimeout(() => commentTextAreaContainerRef.current.value = "", 500);
            fetchRequestComments();
        } catch (error: any) {
            BaseService.reportError(error);
            if (successfulUploads.length) {
                consusService.deleteFiles(successfulUploads).catch(BaseService.reportError);
            }
        }
        sendButtonManageRef.current?.setLoadingState(false);
    }

}

export default TicketRequest;
